import { CommonModule } from '@angular/common';
import { Component, input, output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { EventDto } from '@millionshow/api-client';
import { Fieldset, FormatInputDirective } from '@millionshow/client-libs';
import {
  RegistrationAgeGroup,
  RegistrationGender,
  RegistrationRegionGroup,
  RegistrationVisitMotiveSelectionMode,
} from '@millionshow/common';

@Component({
  selector: 'app-registration-info',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, Fieldset, FormatInputDirective],
  template: `
    <form [formGroup]="form()">
      <div class="flex flex-col gap-6">
        <div
          class="flex flex-col gap-3 border border-zinc-100 rounded-2xl p-5 bg-white">
          <h2 class="text-lg font-semibold text-zinc-800">행사 정보</h2>

          <app-fieldset label="행사 선택" required>
            <select
              formControlName="eventId"
              class="select cursor-pointer w-full">
              <option value="">선택해주세요</option>
              @for (event of events(); track event.id) {
              <option [value]="event.id">{{ event.title }}</option>
              }
            </select>
          </app-fieldset>
        </div>
        <div
          class="flex flex-col gap-6 border border-zinc-100 rounded-2xl p-5 bg-white">
          <h2 class="text-lg font-semibold text-zinc-800">신청자 정보</h2>

          <app-fieldset label="이름" required>
            <input
              type="text"
              formControlName="name"
              class="input w-full"
              placeholder="입력해주세요" />
          </app-fieldset>

          <app-fieldset label="연락처" required>
            <input
              type="text"
              formControlName="contact"
              appFormatInput="phone"
              placeholder="010-1234-5678"
              maxlength="13"
              class="input w-full" />
          </app-fieldset>

          @if (showGender()) {
          <app-fieldset
            label="성별"
            [required]="isGenderRequired() ? 'true' : 'false'">
            <div class="flex flex-wrap gap-2">
              @for (option of genderOptions(); track option.value) {
              <button
                type="button"
                class="choice-chip"
                [class.choice-chip-active]="isGenderSelected(option.value)"
                (click)="selectGender.emit(option.value)">
                {{ option.label }}
              </button>
              }
            </div>
          </app-fieldset>
          } @if (showAgeGroup()) {
          <app-fieldset
            label="나이"
            [required]="isAgeGroupRequired() ? 'true' : 'false'">
            <div class="flex flex-wrap gap-2">
              @for (option of ageGroupOptions(); track option.value) {
              <button
                type="button"
                class="choice-chip"
                [class.choice-chip-active]="isAgeGroupSelected(option.value)"
                (click)="selectAgeGroup.emit(option.value)">
                {{ option.label }}
              </button>
              }
            </div>
          </app-fieldset>
          } @if (showRegionGroup()) {
          <app-fieldset
            label="지역"
            [required]="isRegionGroupRequired() ? 'true' : 'false'">
            <div class="flex flex-wrap gap-2">
              @for (option of regionGroupOptions(); track option.value) {
              <button
                type="button"
                class="choice-chip"
                [class.choice-chip-active]="isRegionGroupSelected(option.value)"
                (click)="selectRegionGroup.emit(option.value)">
                {{ option.label }}
              </button>
              }
            </div>
          </app-fieldset>
          }
        </div>

        @if (showVisitMotives()) {
        <div
          class="flex flex-col gap-4 border border-zinc-100 rounded-2xl p-5 bg-white">
          <div class="flex flex-col gap-1">
            <div class="flex items-center gap-1">
              <h2 class="text-lg font-semibold text-zinc-800">관람동기</h2>
              @if (isVisitMotivesRequired()) {
              <span
                class="bg-primary-500 w-1.5 h-1.5 rounded-full inline-block"></span>
              }
            </div>
            <p class="text-sm text-zinc-400">이 행사를 어떻게 알게 되셨나요?</p>
          </div>

          <div class="flex flex-wrap gap-2">
            @for (option of awarenessOptions(); track option.value) {
            <button
              type="button"
              (click)="toggleAwareness.emit(option.value)"
              class="choice-chip"
              [class.choice-chip-active]="isAwarenessSelected(option.value)">
              {{ option.label }}
            </button>
            }
          </div>

          @if (showAwarenessError()) {
          <p class="text-sm text-red-500">관람동기를 선택해주세요.</p>
          }
        </div>
        }
      </div>
    </form>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .choice-chip {
        padding: 0.875rem 1.5rem;
        border-radius: 9999px;
        font-size: 0.95rem;
        font-weight: 600;
        border: 2px solid #e4e4e7;
        cursor: pointer;
        transition: all 0.15s ease;
        white-space: nowrap;
        background: white;
        color: #52525b;
      }

      .choice-chip-active {
        background: #ff6b00;
        border-color: #ff6b00;
        color: white;
      }
    `,
  ],
})
export class RegistrationInfoComponent {
  form = input.required<FormGroup>();
  events = input<EventDto[]>([]);
  genderOptions = input<{ value: RegistrationGender; label: string }[]>([]);
  ageGroupOptions = input<{ value: RegistrationAgeGroup; label: string }[]>([]);
  regionGroupOptions = input<
    { value: RegistrationRegionGroup; label: string }[]
  >([]);
  awarenessOptions = input<{ value: string; label: string }[]>([]);
  selectedMotives = input<string[]>([]);
  showAwarenessError = input(false);
  showGender = input(true);
  isGenderRequired = input(true);
  showAgeGroup = input(true);
  isAgeGroupRequired = input(true);
  showRegionGroup = input(true);
  isRegionGroupRequired = input(true);
  showVisitMotives = input(true);
  isVisitMotivesRequired = input(true);
  visitMotiveSelectionMode = input<RegistrationVisitMotiveSelectionMode>(
    RegistrationVisitMotiveSelectionMode.MULTIPLE
  );
  selectGender = output<RegistrationGender>();
  selectAgeGroup = output<RegistrationAgeGroup>();
  selectRegionGroup = output<RegistrationRegionGroup>();
  toggleAwareness = output<string>();

  isGenderSelected(gender: RegistrationGender): boolean {
    return this.form().get('gender')?.value === gender;
  }

  isAgeGroupSelected(ageGroup: RegistrationAgeGroup): boolean {
    return this.form().get('ageGroup')?.value === ageGroup;
  }

  isRegionGroupSelected(regionGroup: RegistrationRegionGroup): boolean {
    return this.form().get('regionGroup')?.value === regionGroup;
  }

  isAwarenessSelected(optionId: string): boolean {
    return this.selectedMotives().includes(optionId);
  }
}
